import { API_BASE_URL } from "./config.js";
import { readSelectedTenant } from "./tenantStorage.js";

/** Fehlerform des Backends (system/ApiError.ts). */
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, string>;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, string>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
  }
}

export type TokenSource = () => string | null;

let accessToken: TokenSource = () => null;

/** Der AuthProvider hinterlegt hier, woher das aktuelle Zugriffstoken kommt. */
export function setTokenSource(source: TokenSource): void {
  accessToken = source;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Überschreibt den Mandanten aus dem lokalen Speicher. */
  tenant?: string | null;
  signal?: AbortSignal;
}

/**
 * Einziger Weg zur API. Setzt Token und Mandanten-Header und übersetzt Fehlerantworten
 * in ApiRequestError, damit React Query und die Oberfläche sie einheitlich behandeln.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers({ Accept: "application/json" });

  const token = accessToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const tenant = options.tenant === undefined ? readSelectedTenant() : options.tenant;
  if (tenant) {
    headers.set("X-Buma-Tenant", tenant);
  }

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) {
    init.signal = options.signal;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, init);

  if (!response.ok) {
    throw new ApiRequestError(response.status, await readErrorBody(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    const parsed = (await response.json()) as Partial<ApiErrorBody>;
    if (typeof parsed.message === "string" && typeof parsed.code === "string") {
      return parsed as ApiErrorBody;
    }
  } catch {
    // Antwort ohne verwertbaren Körper — unten steht eine allgemeine Meldung.
  }
  return { code: "internal", message: "Die Anfrage ist fehlgeschlagen." };
}
